
import React, { useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Dialog,
    DialogContent,
} from "@mui/material";
import { styled } from "@mui/system";
import CustomButton from "../../components/buttons/CustomButton";
import CustomTooltip from "../../../utils/CustomTooltip";
import styles from "./table.module.css";

const StyledTableCell = styled(TableCell)(({ theme, frozen }) => ({
    "&.MuiTableCell-head": {
        fontFamily: "Unilever Shilling",
        backgroundColor: "#2036C7",
        color: "#fff",
        textAlign: "center",
        padding: "5px",
        fontSize: "12px",
        fontWeight: 400,
        border: "1px solid #EAECF0",
        ...(frozen && {
            position: "sticky",
            left: frozen.left,
            zIndex: 3,
            backgroundColor: "#2036C7",
        }),
    },
    "&.MuiTableCell-body": {
        fontFamily: "Unilever Shilling",
        fontSize: 12,
        fontWeight: 400,
        padding: "5px",
        color: "#2B2B2B",
        border: "1px solid #EAECF0",
        textAlign: "center",
        ...(frozen && {
            position: "sticky",
            left: frozen.left,
            zIndex: 2,
            backgroundColor: "#fff",
        }),
    },
}));

const StyledTableRow = styled(TableRow)({});

const SupplierTable = ({ data, pagination, onPageChange, onRowsPerPageChange, filters = {}, poDetails = {}, rmpmFiltersOption = {}, tableType = "" }) => {
    const columns = data?.length > 0 ? Object.keys(data[0]) : [];

    // Only apply freezing for SOB Compliance table
    const shouldFreeze = tableType === "sob";

    // Define column widths for frozen columns (adjust these as needed)
    const columnWidths = {
        0: 100,
        1: 120,
        2: 120,
        3: 120,
        4: 120,
        5: 120,
    };

    // Calculate left position for each frozen column
    const getFrozenStyle = (index) => {
        if (!shouldFreeze || index >= 6) return null;

        let left = 0;
        for (let i = 0; i < index; i++) {
            left += columnWidths[i] || 100;
        }
        return { left: `${left}px` };
    };

    const [popupOpen, setPopupOpen] = useState(false);
    const [popupData, setPopupData] = useState([]);

    const handleCellClick = (row) => {
        const vendorId = row["Vendor ID"];
        const siteCode = row["Site code"];
        const key = `${vendorId}|${siteCode}`;

        let detailRows = poDetails[key] || [];

        // Apply filters (passed from SupplierInsights)
        Object.entries(filters).forEach(([label, selectedValues]) => {
            if (selectedValues?.length > 0) {
                detailRows = detailRows.filter(item => selectedValues.includes(item[label]));
            }
        });
        if (detailRows.length > 0) {
            setPopupData(detailRows);
            setPopupOpen(true);
        }
    };

    return (
        <>
            <TableContainer className={styles.scrollbarContainer} sx={{ overflowX: "auto" }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            {columns?.map((col, index) => (
                                <StyledTableCell
                                    key={index}
                                    frozen={getFrozenStyle(index)}
                                    sx={{
                                        minWidth: index < 6 ? `${columnWidths[index] || 100}px` : 'auto',
                                        maxWidth: index < 6 ? `${columnWidths[index] || 100}px` : 'auto',
                                    }}
                                >
                                    {col}
                                </StyledTableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data?.length > 0 ? (
                            data?.map((alert, rowIndex) => (
                                <StyledTableRow key={rowIndex}>
                                    {columns.map((col, colIndex) => {
                                        const isClickable = col === "No. of Over due PO";
                                        const isPercentageColumn = ["Overdue PO%", "OTIF", "QC index"].includes(col);
                                        const isSOBCompliance = col === "SOB Compliance";
                                        const isActualLeadTime = col === "Actual LT";

                                        const masterLeadTime = parseFloat(alert["Master LT"] ?? 0);
                                        const actualLeadTime = parseFloat(alert["Actual LT"] ?? 0);

                                        const shouldHighlightRed =
                                            isActualLeadTime &&
                                            actualLeadTime > masterLeadTime * 1.25;

                                        let sobComplianceValue = null;
                                        let shouldHighlightSOBRed = false;

                                        if (isSOBCompliance && alert[col] !== null && alert[col] !== undefined) {
                                            sobComplianceValue = parseFloat(alert[col]);
                                            shouldHighlightSOBRed = !(sobComplianceValue >= -10 && sobComplianceValue <= 10);
                                        }

                                        let cellValue = alert[col];
                                        if (isPercentageColumn && cellValue !== null && cellValue !== undefined && cellValue !== "") {
                                            cellValue = `${cellValue}%`;
                                        }

                                        if (col === "Material description" || col === "Vendor name") {
                                            return (
                                                <StyledTableCell
                                                    key={colIndex}
                                                    className={styles.ellipsisText}
                                                    frozen={getFrozenStyle(colIndex)}
                                                    style={{
                                                        maxWidth: "150px",
                                                        minWidth: colIndex < 6 ? `${columnWidths[colIndex] || 100}px` : 'auto',
                                                    }}
                                                >
                                                    <CustomTooltip title={alert[col]}>
                                                        <span>{cellValue}</span>
                                                    </CustomTooltip>
                                                </StyledTableCell>
                                            );
                                        }

                                        return (
                                            <StyledTableCell
                                                key={colIndex}
                                                align="right"
                                                frozen={getFrozenStyle(colIndex)}
                                                style={{
                                                    color: (col === "No. of Over due PO") ? "var(--primary-unilever-color)" : "",
                                                    fontWeight: (col === "No. of Over due PO") ? 500 : "",
                                                    cursor: (col === "No. of Over due PO") ? "pointer" : "",
                                                    backgroundColor: shouldHighlightRed
                                                        ? "rgba(240, 77, 78, 0.76)"
                                                        : shouldHighlightSOBRed
                                                            ? "rgba(240, 77, 78, 0.76)"
                                                            : colIndex < 5 ? "#fff" : "",
                                                    minWidth: colIndex < 5 ? `${columnWidths[colIndex] || 100}px` : 'auto',
                                                }}
                                                onClick={() => isClickable && handleCellClick(alert)}
                                            >
                                                {cellValue}
                                            </StyledTableCell>
                                        );
                                    })}
                                </StyledTableRow>
                            ))
                        ) : (
                            <StyledTableRow>
                                <StyledTableCell colSpan={columns.length || 1} sx={{ whiteSpace: "nowrap" }}>
                                    Data not available...
                                </StyledTableCell>
                            </StyledTableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[]}
                component="div"
                count={pagination.total || 0}
                rowsPerPage={pagination.per_page || 10}
                page={(pagination.page || 1) - 1}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
                sx={{
                    padding: "8px",
                    "& .MuiTablePagination-toolbar": {
                        minHeight: "20px",
                        fontSize: "12px",
                    },
                    "& .MuiTablePagination-select": {
                        fontSize: "12px",
                    },
                    "& .MuiTablePagination-selectLabel": {
                        fontSize: "12px",
                    },
                    "& .MuiTablePagination-displayedRows": {
                        fontSize: "12px",
                    },
                    "& .MuiTablePagination-actions": {
                        marginLeft: "10px",
                    },
                    "& .MuiIconButton-root": {
                        fontSize: "14px",
                    },
                    "& .MuiSvgIcon-root": {
                        height: "14px",
                        width: "14px",
                        fontSize: "14px",
                    },
                }}
            />

            <Dialog open={popupOpen} onClose={() => setPopupOpen(false)} maxWidth="lg" fullWidth>
                <DialogContent sx={{ height: "calc(100vh - 100px)", overflow: "auto" }}>
                    {popupData.length > 0 ? (
                        <TableContainer className={styles.scrollbarContainer} >
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <StyledTableRow>
                                        {Object.keys(popupData[0]).map((col, index) => (
                                            <StyledTableCell key={index}>{col}</StyledTableCell>
                                        ))}
                                    </StyledTableRow>
                                </TableHead>
                                <TableBody>
                                    {popupData.map((row, i) => (
                                        <StyledTableRow key={i}>
                                            {Object.values(row).map((val, index) => (
                                                <StyledTableCell key={index}>{val}</StyledTableCell>
                                            ))}
                                        </StyledTableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <StyledTableRow>
                            <StyledTableCell colSpan={columns.length || 1}
                                sx={{ whiteSpace: "nowrap" }}>
                                No overdue PO details found...
                            </StyledTableCell>
                        </StyledTableRow>
                    )}
                </DialogContent>
                <div className={styles.backButton}>
                    <CustomButton
                        label="Go Back"
                        type="back"
                        onClick={() => setPopupOpen(false)}
                    />
                </div>
            </Dialog >
        </>
    );
};

export default SupplierTable;
